/**
 * MetricGoalBars — Barras por día con eje Y de guías para tarjetas KPI.
 *
 * QUÉ HACE: pinta una mini-gráfica de barras (una por día, más antiguo → hoy) con
 *   un **eje Y de 3 niveles** en el gutter izquierdo: tope (meta o máximo),
 *   guía intermedia (mitad del eje) y **línea base** ($0) al pie. Cada barra
 *   tiene un **tooltip** (hover/tap) con el monto exacto. Cuando se pasa `goal`,
 *   la barra de "hoy" se pinta verde (emerald) al alcanzarla/superarla y azul
 *   secundaria mientras sigue por debajo; sin `goal` (p. ej. "Ventas Hoy") la
 *   barra de hoy usa el mismo azul sólido. La usan "Meta del Día" y "Ventas Hoy".
 * QUÉ INVOCA: `useAnimatedSeries` (subida/bajada de las alturas).
 * QUÉ EXPORTA: MetricGoalBars, MetricGoalBarPoint.
 * QUÉ NO DEBE HACER: no calcula métricas ni accede a datos.
 */
import { useAnimatedSeries } from "@/hooks/useAnimatedSeries";
import { MOTION } from "@/lib/animation/motion";
import { formatChartAxisCop } from "@/lib/utils/formatters";

/** Punto diario de la gráfica con eje de guías. */
export interface MetricGoalBarPoint {
  /** Etiqueta del día ("Lu", "Hoy"). */
  label: string;
  /** Valor del día (caja + web). */
  value: number;
  /** Etiqueta formateada del valor (tooltip). */
  valueLabel?: string;
  /** true solo para el punto del día actual. */
  isToday: boolean;
}

interface MetricGoalBarsProps {
  points: MetricGoalBarPoint[];
  /** Meta opcional: dibuja su línea y da color semántico a la barra de hoy. */
  goal?: number;
  /** Etiqueta de la meta (si se omite, se formatea el valor). */
  goalLabel?: string;
  /** Clases del relleno de los días previos. */
  barClassName?: string;
  /** Clases del relleno de hoy cuando aún no alcanza la meta. */
  pendingClassName?: string;
  /** Clases del relleno de hoy cuando alcanza o supera la meta. */
  reachedClassName?: string;
  /**
   * Si la barra de hoy se colorea según la meta (verde al alcanzarla). En
   * "Ventas Hoy" se usa `false` para mostrar la meta solo como referencia del
   * eje sin cambiar el color de la barra.
   */
  colorTodayByGoal?: boolean;
  className?: string;
}

export const MetricGoalBars = ({
  points,
  goal,
  barClassName = "bg-blue-600/30 dark:bg-blue-400/30",
  pendingClassName = "bg-blue-600 dark:bg-blue-400",
  reachedClassName = "bg-emerald-500",
  colorTodayByGoal = true,
  className = "",
}: MetricGoalBarsProps) => {
  const animatedPoints = useAnimatedSeries(points, {
    keys: ["value"],
    duration: MOTION.barRise,
    stagger: MOTION.barRise,
  });
  const maxValue = points.reduce((top, point) => Math.max(top, point.value), 0);
  const hasGoal = typeof goal === "number" && goal > 0;
  /** Tope del eje: la meta si existe, si no el mayor valor. */
  const axisTop = Math.max(maxValue, hasGoal ? goal : 0, 1);
  /** Las barras escalan al tope del eje (la más alta llega a la línea superior). */
  const scaleMax = axisTop;
  const pct = (value: number): number =>
    Math.max(0, Math.min(100, (value / scaleMax) * 100));

  /**
   * Eje Y **proporcional** a la altura real del gráfico: tope (máximo) al 100 %
   * y punto medio (50 % del máximo) al 50 %. La base ($0) va en el borde
   * inferior. Las etiquetas viven en el gutter izquierdo, fuera de la línea.
   */
  const topLabel = formatChartAxisCop(axisTop);
  const midLabel = formatChartAxisCop(axisTop / 2);

  const lineClass =
    "border-t border-dashed border-slate-200 dark:border-slate-700";

  return (
    <div className={className}>
      <div className="relative h-9 sm:h-12">
        {/* Etiquetas del eje Y en un gutter fijo; la línea empieza después. */}
        <div className="absolute inset-y-0 left-0 w-8 text-right text-[7px] font-normal tabular-nums text-slate-400 sm:w-9 sm:text-[8px] dark:text-slate-500">
          <span className="absolute right-1.5 top-0 sm:right-2">
            {topLabel}
          </span>
          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 sm:right-2">
            {midLabel}
          </span>
          <span className="absolute right-1.5 bottom-0 translate-y-1/2 sm:right-2">
            {formatChartAxisCop(0)}
          </span>
        </div>

        {/* Guías punteadas uniformemente distribuidas (tope, media y base). */}
        <div className="pointer-events-none absolute inset-y-0 left-8 right-0 z-10 sm:left-9">
          <div className={`absolute inset-x-0 top-0 ${lineClass}`} />
          <div className={`absolute inset-x-0 top-1/2 ${lineClass}`} />
          <div className={`absolute inset-x-0 bottom-0 ${lineClass}`} />
        </div>

        <div className="flex h-full items-end justify-between gap-1 pl-8 sm:pl-9">
          {points.map((point, index) => {
            const animatedValue = animatedPoints[index]?.value ?? point.value;
            const heightPct = Math.max(
              animatedValue > 0 ? 4 : 0,
              Math.round(pct(animatedValue)),
            );
            const reached = colorTodayByGoal && hasGoal && point.value >= goal;
            const fill = point.isToday
              ? reached
                ? reachedClassName
                : pendingClassName
              : barClassName;
            return (
              <div
                key={`${point.label}-${index}`}
                className="flex h-full min-w-0 flex-1 items-end"
              >
                <div
                  className={`group relative w-full rounded-t-md transition-colors duration-300 ${fill}`}
                  style={{ height: `${heightPct}%` }}
                >
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-1.5 py-0.5 text-[6px] font-semibold tabular-nums text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-active:opacity-100 dark:bg-slate-100 dark:text-slate-900"
                  >
                    {point.valueLabel ?? point.value}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-1 flex justify-between gap-1 pl-8 sm:pl-9">
        {points.map((point, index) => (
          <span
            key={`${point.label}-${index}`}
            className={`min-w-0 flex-1 truncate text-center text-[6px] font-medium sm:text-[7px] ${
              point.isToday
                ? "font-semibold text-slate-700 dark:text-slate-200"
                : "text-slate-500 dark:text-slate-400"
            }`}
          >
            {point.label}
          </span>
        ))}
      </div>
    </div>
  );
};
